import { useCallback, useEffect, useState } from 'react'
import { Fingerprint, KeyRound, Plus, ShieldCheck, Trash2, Loader2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { EmptyState, ErrorState, Skeleton } from '@/components/States'
import { RelativeTime } from '@/components/DataDisplay'
import { useToast } from '@/providers/UiProviders'
import { ApiError } from '@/lib/api'
import {
  fetchSecurityKeys,
  beginSecurityKeyRegistration,
  createBrowserCredential,
  finishSecurityKeyRegistration,
  revokeSecurityKey,
  type SecurityKeyRow,
} from './authAdminApi'

/**
 * A1.02 — Аппаратный 2FA (WebAuthn/FIDO2). REAL: lists the current admin's
 * registered security keys, registers a new one via a genuine
 * `navigator.credentials.create()` ceremony (begin → browser → finish, verified
 * server-side by go-webauthn), and revokes per-key. Empty list until a key is
 * registered is honest — no mock. Strings inline (Uzbek), i18n/* untouched.
 */

// Inline Uzbek strings (i18n/* HOT — kalitlarga keyin ko'chiriladi).
const S = {
  title: 'Apparat 2FA (xavfsizlik kalitlari)',
  desc: 'Admin panelга kirish uchun WebAuthn/FIDO2 kalitlari',
  addKey: 'Xavfsizlik kaliti qo`shish',
  infoTitle: 'Apparat kaliti nima?',
  infoBody:
    'WebAuthn/FIDO2 kaliti (YubiKey, Touch ID, Windows Hello) — parolga qo`shimcha, fishingga chidamli ikkinchi omil. Kalit maxfiy kaliti qurilmangizdan chiqmaydi; server faqat ochiq kalitni saqlaydi.',
  emptyTitle: 'Hali kalit ro`yxatdan o`tmagan',
  emptyBody: 'Hisobingizni himoyalash uchun apparat xavfsizlik kalitini qo`shing.',
  loadError: 'Kalitlar ro`yxatini yuklab bo`lmadi',
  unknownModel: 'Noma`lum model',
  added: 'Qo`shildi',
  lastUsed: 'Oxirgi ishlatilgan',
  neverUsed: 'Hali ishlatilmagan',
  revoke: 'Bekor qilish',
  revoked: 'Kalit bekor qilindi',
  revokeTitle: 'Kalitni bekor qilishmi?',
  revokeBody: 'Bu kalit endi kirish uchun ishlamaydi. Amalni qaytarib bo`lmaydi.',
  cancel: 'Bekor qilish',
  // add dialog
  dialogTitle: 'Xavfsizlik kalitini ro`yxatdan o`tkazish',
  dialogBody:
    'Kalitga nom bering, so`ng «Ro`yxatdan o`tkazish»ni bosing va qurilmangiz so`raganда kalitga teging.',
  nameLabel: 'Kalit nomi',
  namePlaceholder: 'Masalan: YubiKey 5C',
  register: 'Ro`yxatdan o`tkazish',
  registering: 'Ro`yxatdan o`tkazilmoqda…',
  registered: 'Kalit ro`yxatdan o`tdi',
  // errors
  notSupported:
    'Bu brauzer WebAuthn`ni qo`llab-quvvatlamaydi yoki sahifa HTTPS`da emas.',
  ceremonyCancelled: 'Amal bekor qilindi',
  keyExists: 'Bu kalit allaqachon ro`yxatdan o`tgan',
  failed: 'Bajarib bo`lmadi',
}

const webauthnSupported = () =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function'

export function HardwareTwoFAScreen() {
  const { toast } = useToast()
  const [keys, setKeys] = useState<SecurityKeyRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<SecurityKeyRow | null>(null)
  const [revoking, setRevoking] = useState(false)

  const load = useCallback((signal?: AbortSignal) => {
    setError(null)
    fetchSecurityKeys(signal)
      .then((rows) => setKeys(rows))
      .catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') return
        setKeys([])
        setError(e instanceof ApiError ? e.code : 'ERROR')
      })
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  const openAdd = useCallback(() => {
    if (!webauthnSupported()) {
      toast(S.notSupported, { tone: 'error' })
      return
    }
    setName('')
    setAddOpen(true)
  }, [toast])

  const register = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      const options = await beginSecurityKeyRegistration()
      const attestation = await createBrowserCredential(options)
      await finishSecurityKeyRegistration(name.trim(), attestation)
      toast(S.registered, { tone: 'success' })
      setAddOpen(false)
      load()
    } catch (e) {
      // User dismissed the native prompt, or the authenticator is already enrolled.
      if (e instanceof DOMException) {
        if (e.name === 'NotAllowedError' || e.name === 'AbortError') {
          toast(S.ceremonyCancelled, { tone: 'info' })
        } else if (e.name === 'InvalidStateError') {
          toast(S.keyExists, { tone: 'error' })
        } else {
          toast(S.failed, { tone: 'error' })
        }
      } else if (e instanceof Error && e.message === 'cancelled') {
        toast(S.ceremonyCancelled, { tone: 'info' })
      } else if (e instanceof ApiError) {
        toast(e.code === 'CONFLICT' ? S.keyExists : e.message || S.failed, { tone: 'error' })
      } else {
        toast(S.failed, { tone: 'error' })
      }
    } finally {
      setBusy(false)
    }
  }, [busy, name, toast, load])

  const confirmRevoke = useCallback(async () => {
    if (!revokeTarget || revoking) return
    setRevoking(true)
    try {
      await revokeSecurityKey(revokeTarget.id)
      toast(S.revoked, { tone: 'success' })
      setRevokeTarget(null)
      load()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setRevoking(false)
    }
  }, [revokeTarget, revoking, toast, load])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A1.02"
        description={S.desc}
        verified
        actions={
          <Button variant="primary" size="sm" onClick={openAdd}>
            <Plus size={14} />
            {S.addKey}
          </Button>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <div className="mx-auto w-full max-w-[720px] space-y-4">
          {/* Info banner (honest explanation, not a "planned" stub) */}
          <div className="flex items-start gap-3 rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-subtle text-accent">
              <ShieldCheck size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-primary">{S.infoTitle}</h2>
              <p className="mt-0.5 text-sm text-secondary">{S.infoBody}</p>
            </div>
          </div>

          {/* Body: loading / error / empty / list */}
          {keys === null ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[68px] w-full rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <ErrorState title={S.loadError} code={error} onRetry={() => load()} />
          ) : keys.length === 0 ? (
            <EmptyState
              icon={<Fingerprint size={28} strokeWidth={1.5} />}
              title={S.emptyTitle}
              description={S.emptyBody}
              action={
                <Button variant="primary" size="sm" onClick={openAdd}>
                  <Plus size={14} />
                  {S.addKey}
                </Button>
              }
            />
          ) : (
            <ul className="space-y-2">
              {keys.map((k) => (
                <li
                  key={k.id}
                  className="flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 p-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-3 text-secondary">
                    <KeyRound size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-primary">{k.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-tertiary">
                      <span className="font-mono">{k.aaguid || S.unknownModel}</span>
                      <span>
                        {S.added}: <RelativeTime value={k.created_at} />
                      </span>
                      <span>
                        {S.lastUsed}:{' '}
                        {k.last_used_at ? <RelativeTime value={k.last_used_at} /> : S.neverUsed}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-danger hover:bg-danger-subtle"
                    onClick={() => setRevokeTarget(k)}
                  >
                    <Trash2 size={14} />
                    {S.revoke}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Add key dialog */}
      <Dialog
        open={addOpen}
        onClose={() => (busy ? undefined : setAddOpen(false))}
        title={S.dialogTitle}
        description={S.dialogBody}
        icon={<Fingerprint size={18} />}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)} disabled={busy}>
              {S.cancel}
            </Button>
            <Button variant="primary" size="sm" onClick={register} disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              {busy ? S.registering : S.register}
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-secondary">{S.nameLabel}</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={S.namePlaceholder}
            maxLength={64}
            autoFocus
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) register()
            }}
          />
        </label>
      </Dialog>

      {/* Revoke confirm dialog */}
      <Dialog
        open={revokeTarget !== null}
        onClose={() => (revoking ? undefined : setRevokeTarget(null))}
        title={S.revokeTitle}
        description={revokeTarget ? `${revokeTarget.name} — ${S.revokeBody}` : S.revokeBody}
        icon={<Trash2 size={18} />}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(null)} disabled={revoking}>
              {S.cancel}
            </Button>
            <Button variant="danger" size="sm" onClick={confirmRevoke} disabled={revoking}>
              {revoking ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {S.revoke}
            </Button>
          </>
        }
      />
    </>
  )
}
